import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button, Input, Radio, Space, Typography } from 'antd';
import { ClearOutlined, EditOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const { Text } = Typography;

const colors = {
  primary: '#4A90E2',
  secondary: '#7FB3D5',
  accent: '#D4E6F1',
  text: '#2C3E50',
  border: '#d9d9d9',
  background: '#F5F9FC',
  white: '#FFFFFF',
  error: '#d9534f'
};

const SignatureContainer = styled.div`
  margin-bottom: 16px;
`;

const SignatureTypeToggle = styled.div`
  margin-bottom: 16px;
  
  .ant-radio-button-wrapper {
    height: 40px;
    line-height: 38px;
    padding: 0 20px;
    
    &:first-child {
      border-radius: 8px 0 0 8px;
    }
    
    &:last-child {
      border-radius: 0 8px 8px 0;
    }
  }
  
  .ant-radio-button-wrapper-checked {
    background-color: ${colors.primary};
    border-color: ${colors.primary};
    color: white;
    
    &:hover {
      background-color: ${colors.secondary};
      border-color: ${colors.secondary};
    }
  }
`;

const CanvasContainer = styled.div`
  border: 2px solid ${props => props.$hasError ? colors.error : colors.border};
  border-radius: 8px;
  background: ${colors.white};
  overflow: hidden;
  
  &:focus-within {
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const CanvasWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 150px;
  background: ${colors.white};
  
  canvas {
    width: 100% !important;
    height: 100% !important;
    touch-action: none;
  }
`;

const PlaceholderText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #bbb;
  font-size: 14px;
  pointer-events: none;
  user-select: none;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px;
  background: ${colors.background};
  border-top: 1px solid ${colors.border};
`;

const TypedSignatureInput = styled(Input)`
  height: 60px;
  font-size: 24px;
  font-family: 'Brush Script MT', 'Segoe Script', cursive;
  font-style: italic;
  text-align: center;
  border: 2px solid ${props => props.$hasError ? colors.error : colors.border};
  border-radius: 8px;
  
  &:focus {
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const SignaturePad = ({ 
  onChange, 
  signatureType = 'draw',
  onSignatureTypeChange,
  typedSignature = '',
  onTypedSignatureChange,
  hasError = false 
}) => {
  const sigPadRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const handleClear = () => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
      setIsEmpty(true);
      onChange('');
    }
  };

  const handleEnd = () => {
    if (sigPadRef.current) {
      // Use getCanvas() instead of getTrimmedCanvas() to avoid trim-canvas compatibility issues
      const canvas = sigPadRef.current.getCanvas();
      const dataUrl = canvas.toDataURL('image/png');
      setIsEmpty(sigPadRef.current.isEmpty());
      onChange(dataUrl);
    }
  };

  const handleBegin = () => {
    setIsEmpty(false);
  };

  return (
    <SignatureContainer>
      <SignatureTypeToggle>
        <Radio.Group 
          value={signatureType} 
          onChange={(e) => onSignatureTypeChange(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="draw">
            <EditOutlined /> Draw Signature
          </Radio.Button>
          <Radio.Button value="type">
            <EditOutlined /> Type Signature
          </Radio.Button>
        </Radio.Group>
      </SignatureTypeToggle>

      {signatureType === 'draw' ? (
        <CanvasContainer $hasError={hasError}>
          <CanvasWrapper>
            {isEmpty && (
              <PlaceholderText>
                Sign here with your mouse or finger
              </PlaceholderText>
            )}
            <SignatureCanvas
              ref={sigPadRef}
              canvasProps={{
                className: 'signature-canvas'
              }}
              penColor="black"
              backgroundColor="white"
              onEnd={handleEnd}
              onBegin={handleBegin}
            />
          </CanvasWrapper>
          <ButtonRow>
            <Button 
              icon={<ClearOutlined />} 
              onClick={handleClear}
              size="small"
            >
              Clear
            </Button>
          </ButtonRow>
        </CanvasContainer>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          <TypedSignatureInput
            placeholder="Type your full name"
            value={typedSignature}
            onChange={(e) => onTypedSignatureChange(e.target.value)}
            $hasError={hasError}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Your typed name will serve as your electronic signature
          </Text>
        </Space>
      )}
    </SignatureContainer>
  );
};

export default SignaturePad;
